<template>
    <div class="flex flex-col gap-2">
        <form class="flex items-center gap-2" @submit.prevent="ask.ask()">
            <div class="relative min-w-0 flex-1">
                <input
                    v-model="ask.question"
                    v-online
                    type="text"
                    :maxlength="ask.maxLength"
                    :disabled="ask.busy"
                    :placeholder="ask.listening ? 'Listening…' : placeholder"
                    class="block w-full rounded-lg border border-gray-300 py-2.5 pl-4 text-sm shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50"
                    :class="ask.speechSupported ? 'pr-11' : 'pr-4'"
                />

                <!-- Hidden rather than disabled where the browser has no
                     implementation: a mic that never works is worse than no
                     mic. Firefox is the case. -->
                <button
                    v-if="ask.speechSupported"
                    type="button"
                    :title="ask.listening ? 'Stop dictating' : 'Dictate your question'"
                    class="absolute inset-y-0 right-0 flex items-center rounded-r-lg px-3"
                    :class="ask.listening ? 'text-red-600' : 'text-gray-400 hover:text-indigo-600'"
                    @click="ask.toggleDictation(locale)"
                >
                    <!-- The pulse is the only signal that the microphone is
                         actually open, so it is not decoration. -->
                    <span v-if="ask.listening" class="absolute inset-y-0 right-2.5 my-auto size-7 animate-ping rounded-full bg-red-100"></span>
                    <MicrophoneIcon class="relative size-5" aria-hidden="true" />
                    <span class="sr-only">{{ ask.listening ? 'Stop dictating' : 'Dictate your question' }}</span>
                </button>
            </div>

            <button
                v-online
                type="submit"
                :disabled="ask.busy || ask.question.trim().length < 3"
                class="shrink-0 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
                {{ ask.busy ? '…' : 'Ask' }}
            </button>
        </form>

        <!-- Only while dictating, and only where there is a choice to make.
             A language picker sitting there permanently is clutter. -->
        <div v-if="ask.speechSupported && ask.listening" class="flex items-center gap-2 text-xs text-gray-500">
            <span>Speaking in</span>
            <select v-model="locale" class="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-xs" @change="restartDictation">
                <option value="en-US">English</option>
                <option value="ur-PK">اردو</option>
            </select>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { MicrophoneIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
    /** The useAsk() instance this composer writes into. */
    ask: { type: Object, required: true },
    placeholder: { type: String, default: 'Ask about your stock, sales or customers…' },
});

// Remembered across sessions: somebody who dictates in Urdu will do it again,
// and re-picking the language every time is the kind of small friction that
// stops a feature being used.
const STORAGE_KEY = 'inventree_ask_dictation_locale';
const locale = ref(localStorage.getItem(STORAGE_KEY) || 'en-US');

function restartDictation() {
    localStorage.setItem(STORAGE_KEY, locale.value);

    // The recogniser's language is fixed at start(), so switching mid-sentence
    // means stopping and starting again. What was already dictated stays.
    if (props.ask.listening) {
        props.ask.stopDictation();
        setTimeout(() => props.ask.toggleDictation(locale.value), 150);
    }
}
</script>
